// "Bukti pembayaran" — attach and view QRIS / transfer proofs for one sale.
//
// Lives on the invoice page rather than in the POS flow: at a booth the
// cashier takes the money, hands over the goods, and attaches the screenshot
// later, from the transaction list. So this is the one place it happens, and
// it works for any sale regardless of how old it is.

import { useRef, useState } from 'react';
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import { PROOF_ACCEPT } from '@/lib/api/paymentProofs';
import {
  useDeletePaymentProof,
  usePaymentProofs,
  useUploadPaymentProof,
} from '@/hooks/usePaymentProofs';
import { formatDateTime } from '@/lib/format';
import type { AppTransaction } from '@/types/domain';

const METHOD_LABEL: Record<string, string> = { cash: 'Tunai', qris: 'QRIS', transfer: 'Transfer', card: 'Kartu' };

export function PaymentProofsCard({
  transaction,
  canDelete,
}: {
  transaction: AppTransaction;
  canDelete: boolean;
}) {
  const { data: proofs, isLoading } = usePaymentProofs(transaction.id);
  const upload = useUploadPaymentProof();
  const remove = useDeletePaymentProof();
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isVoided = transaction.paymentStatus === 'cancelled';
  const nonCash = transaction.paymentMethod !== 'cash';

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ transactionId: transaction.id, file, note });
      setNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload gagal');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card data-tour="payment-proofs">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-subhead font-display font-semibold text-ink inline-flex items-center gap-2">
          <Paperclip size={16} className="text-ink-subtle" />
          Bukti pembayaran
        </h3>
        <Chip variant={nonCash ? 'info' : 'default'}>{METHOD_LABEL[transaction.paymentMethod] ?? transaction.paymentMethod}</Chip>
      </div>
      <p className="text-caption text-ink-subtle mb-3">
        {nonCash
          ? 'Lampirkan screenshot QRIS atau bukti transfer. Bisa ditambahkan kapan saja setelah transaksi.'
          : 'Pembayaran tunai biasanya tidak butuh bukti — tapi boleh dilampirkan kalau ada.'}
      </p>

      {isLoading ? (
        <p className="text-caption text-ink-subtle">Memuat…</p>
      ) : (proofs?.length ?? 0) === 0 ? (
        <p className="text-caption text-ink-subtle italic mb-3">Belum ada bukti terlampir.</p>
      ) : (
        <ul className="grid grid-cols-3 gap-2 mb-3">
          {proofs!.map((p) => (
            <li key={p.id} className="relative group">
              <a
                href={p.url ?? '#'}
                target="_blank"
                rel="noreferrer"
                title={`${p.note ? p.note + ' · ' : ''}${p.uploadedByName ?? ''} · ${formatDateTime(p.createdAt)}`}
                className="block aspect-square rounded border border-line bg-canvas overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {p.mimeType.startsWith('image/') && p.url ? (
                  <img src={p.url} alt={p.note ?? 'Bukti pembayaran'} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-ink-subtle">
                    <FileText size={20} />
                    <span className="text-[10px] uppercase">PDF</span>
                  </span>
                )}
              </a>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Hapus bukti ini? Tidak bisa dibatalkan.')) {
                      remove.mutateAsync(p).catch((e) => setError(e instanceof Error ? e.message : 'Gagal menghapus'));
                    }
                  }}
                  className="absolute top-1 right-1 w-7 h-7 rounded bg-surface/90 border border-line text-danger opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center"
                  aria-label="Hapus bukti"
                  title="Hapus (owner)"
                >
                  <Trash2 size={12} />
                </button>
              )}
              {p.note && (
                <p className="text-[11px] text-ink-subtle truncate mt-0.5" title={p.note}>{p.note}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {!isVoided && (
        <div className="space-y-2 pt-3 border-t border-line">
          <Input
            placeholder="Catatan (opsional), mis. transfer BCA a.n. Budi"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={upload.isPending}
          />
          <input
            ref={fileRef}
            type="file"
            accept={PROOF_ACCEPT}
            className="hidden"
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            fullWidth
            leftIcon={<Upload size={16} />}
            onClick={() => fileRef.current?.click()}
            loading={upload.isPending}
          >
            Lampirkan bukti
          </Button>
          <p className="text-caption text-ink-subtle">JPG, PNG, WebP, atau PDF — maksimal 5 MB.</p>
        </div>
      )}
      {error && <p role="alert" className="text-caption text-danger mt-2">{error}</p>}
    </Card>
  );
}
